import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import type { OrderLine, Product, SupplierDemandLine } from '@prody/shared';
import {
  DEMAND_ORDER_STATUSES, buildSupplierDemand, groupDemandBySupplier, hasRole,
  mulMoney,
} from '@prody/shared';
import {
  Button, Code, DataTable, EmptyState, IconCrate, ListPane, Metric, PageHeader,
  Popover, Section, Segmented, Select, Stamp, Toolbar, Workbench, toast,
  type Column,
} from '~/design-system';
import { businessPath, path } from '~/app/navigation';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import {
  useAssignSupplier, useGeneratePurchaseTasks, useOrders, useParties,
  useProducts, useQuotes, useStock,
} from '~/platform/data/queries';
import { ORDER_TONE, productName } from '~/platform/domain/presentation';
import './grouping.css';

/* ==================================================================
   THE GROUPING BOARD.

   The morning question is not "what did Sorlí order" — it is "how
   many kilos of Raf does the whole day need, do we already have any,
   and which stand brings the rest". So the board reads per product:
   day total, on hand, to buy, and the supplier who covers it.

   Assigning a supplier here writes the decision onto every open line
   of that product for the day. The suppliers' own sheets and the
   buyer's shopping list are derived from the same rows — one truth,
   three screens.
   ================================================================== */

type DayFilter = 'today' | 'tomorrow';
type ViewMode = 'products' | 'suppliers';

const addDays = (plain: string, days: number): string => {
  const at = new Date(`${plain}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
};

export default function GroupingPage() {
  const { t, i18n } = useTranslation(['grouping', 'orders', 'purchasing', 'common']);
  const { business, format, can } = useBusiness();
  const navigate = useNavigate();
  const [dayFilter, setDayFilter] = useState<DayFilter>('today');
  const [view, setView] = useState<ViewMode>('products');

  const base = businessPath(business.slug);
  const today = format.tradingDay();
  const day = dayFilter === 'today' ? today : addDays(today, 1);

  const { data: orders = [], isLoading } = useOrders(business.id, {
    deliveryDate: day,
    statuses: [...DEMAND_ORDER_STATUSES],
  });
  const { data: products = [] } = useProducts(business.id);
  const { data: parties = [] } = useParties(business.id);
  const { data: stock = [] } = useStock(business.id);
  const { data: quotes = [] } = useQuotes(business.id, today);
  const assign = useAssignSupplier(business.id);
  const generate = useGeneratePurchaseTasks(business.id, day);

  const canAssign = can('purchase', 'update');

  const productsById = useMemo(
    () => new Map<string, Product>(products.map((p) => [p.id as string, p])),
    [products],
  );
  const suppliers = useMemo(() => parties.filter((p) => hasRole(p, 'supplier')), [parties]);
  const suppliersById = useMemo(
    () => new Map(suppliers.map((s) => [s.id as string, s])),
    [suppliers],
  );

  /** line override → product default. The one fallback chain, used everywhere. */
  const resolveSupplier = useMemo(
    () => (line: OrderLine) => {
      if (line.supplierId) {
        const party = suppliersById.get(line.supplierId as string);
        return { id: line.supplierId as string, name: line.supplierName ?? party?.name ?? '—' };
      }
      const fallback = productsById.get(line.productId)?.defaultSupplierId;
      if (!fallback) return null;
      const party = suppliersById.get(fallback as string);
      return party ? { id: party.id as string, name: party.name } : null;
    },
    [productsById, suppliersById],
  );

  const demand = useMemo(
    () => buildSupplierDemand(orders, resolveSupplier),
    [orders, resolveSupplier],
  );
  const groups = useMemo(() => groupDemandBySupplier(demand), [demand]);

  const onHandFor = useMemo(() => {
    const map = new Map<string, number>();
    for (const lot of stock) {
      if (lot.status !== 'available') continue;
      map.set(
        lot.productId as string,
        (map.get(lot.productId as string) ?? 0) + lot.remaining.value,
      );
    }
    return map;
  }, [stock]);

  const costFor = useMemo(
    () => new Map(quotes.map((q) => [q.productId as string, q.cost])),
    [quotes],
  );

  const unassignedCount = demand.filter((row) => row.supplierId === null).length;
  const orderCount = new Set(demand.flatMap((row) => row.orders.map((o) => o.orderId))).size;
  const estimatedCost = demand.reduce((sum, row) => {
    const cost = costFor.get(row.productId);
    return cost ? sum + mulMoney(cost, row.total.value).minor : sum;
  }, 0);

  const assignTo = (row: SupplierDemandLine, supplierId: string) => {
    const supplier = supplierId ? suppliersById.get(supplierId) : undefined;
    assign.mutate(
      {
        deliveryDate: day,
        productId: row.productId,
        supplier: supplier ? { id: supplier.id as string, name: supplier.name } : null,
      },
      {
        onSuccess: (touched) => {
          toast.ok(t('assigned', { product: row.productName }), {
            detail: supplier
              ? `${supplier.name} · ${t('ordersTouched', { count: touched.length })}`
              : t('unassigned'),
          });
        },
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  const sendToBuying = () => {
    generate.mutate(undefined, {
      onSuccess: (tasks) => {
        toast.ok(t('sentToBuying', { count: tasks.length }), {
          detail: format.date(day, 'weekday'),
        });
        void navigate({ to: path(`${base}/purchasing`) });
      },
      onError: () => toast.error(t('common:errors.saveFailed')),
    });
  };

  const demandBreakdown = (row: SupplierDemandLine) => (
    <Popover
      align="start"
      width={300}
      trigger={
        <button type="button" className="pd-group__orders">
          <span className="num">{row.orders.length}</span>
          {t('purchasing:demandFrom')}
        </button>
      }
    >
      <ul className="pd-group__orders-list">
        {row.orders.map((o) => (
          <li key={`${o.orderId}-${o.quantity.value}`}>
            <Code>{o.orderNumber}</Code>
            <span className="truncate">{o.customerName}</span>
            <Stamp tone={ORDER_TONE[o.status]} size="sm">
              {t(`orders:status.${o.status}`)}
            </Stamp>
            <span className="num">{format.quantity(o.quantity)}</span>
          </li>
        ))}
      </ul>
    </Popover>
  );

  const supplierCell = (row: SupplierDemandLine) =>
    canAssign ? (
      <Select
        value={row.supplierId ?? ''}
        aria-label={t('common:labels.supplier')}
        onChange={(e) => assignTo(row, e.target.value)}
      >
        <option value="">{t('unassigned')}</option>
        {suppliers.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
            {s.supplier?.standCode ? ` · ${s.supplier.standCode}` : ''}
          </option>
        ))}
      </Select>
    ) : row.supplierName ? (
      <span>{row.supplierName}</span>
    ) : (
      <Stamp tone="warn" size="sm">{t('unassigned')}</Stamp>
    );

  const columns = useMemo<Column<SupplierDemandLine>[]>(
    () => [
      {
        key: 'product',
        header: t('common:labels.product'),
        width: 'minmax(200px, 1.8fr)',
        sortable: true,
        sortValue: (row) => row.productName,
        cell: (row) => {
          const product = productsById.get(row.productId);
          return (
            <div className="pd-group__product">
              <span className="truncate">
                {product ? productName(product, i18n.resolvedLanguage ?? 'en') : row.productName}
              </span>
              <Code>{row.sku}</Code>
            </div>
          );
        },
      },
      {
        key: 'total',
        header: t('total'),
        align: 'num',
        width: '110px',
        sortable: true,
        sortValue: (row) => row.total.value,
        cell: (row) => <span className="num">{format.quantity(row.total)}</span>,
      },
      {
        key: 'orders',
        header: t('orders:title'),
        width: '150px',
        hideBelow: 'sm',
        cell: demandBreakdown,
      },
      {
        key: 'onhand',
        header: t('purchasing:onHand'),
        align: 'num',
        width: '100px',
        hideBelow: 'md',
        cell: (row) => {
          const onHand = onHandFor.get(row.productId);
          return onHand ? (
            <span className="num">
              {format.quantity({ value: onHand, unit: row.total.unit })}
            </span>
          ) : (
            <span className="faint">—</span>
          );
        },
      },
      {
        key: 'tobuy',
        header: t('purchasing:toBuy'),
        align: 'num',
        width: '110px',
        sortable: true,
        sortValue: (row) => Math.max(0, row.total.value - (onHandFor.get(row.productId) ?? 0)),
        cell: (row) => {
          const toBuy = Math.max(0, row.total.value - (onHandFor.get(row.productId) ?? 0));
          return toBuy > 0 ? (
            <span className="num">{format.quantity({ value: toBuy, unit: row.total.unit })}</span>
          ) : (
            <Stamp tone="ok" size="sm">{t('covered')}</Stamp>
          );
        },
      },
      {
        key: 'cost',
        header: `${t('common:labels.cost')} (${format.currencySymbol()})`,
        align: 'num',
        width: '110px',
        hideBelow: 'lg',
        cell: (row) => {
          const cost = costFor.get(row.productId);
          return cost ? (
            <span className="num">{format.money(mulMoney(cost, row.total.value), { bare: true })}</span>
          ) : (
            <span className="faint">—</span>
          );
        },
      },
      {
        key: 'supplier',
        header: t('common:labels.supplier'),
        width: 'minmax(170px, 1.2fr)',
        sortable: true,
        sortValue: (row) => row.supplierName ?? '',
        cell: supplierCell,
      },
    ],
    // `day` is a real dependency: the assign handler closes over it, and
    // a stale closure would stamp yesterday's date onto tomorrow's board.
    [t, format, productsById, onHandFor, costFor, suppliers, canAssign, i18n.resolvedLanguage, day],
  );

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={format.date(day, 'weekday')}
        actions={
          canAssign ? (
            <Button
              variant="primary"
              icon={<IconCrate />}
              busy={generate.isPending}
              disabled={demand.length === 0}
              onClick={sendToBuying}
            >
              {t('sendToBuying')}
            </Button>
          ) : undefined
        }
      />

      <Toolbar
        trailing={
          <div className="pd-group__meter" data-print="hide">
            <Metric label={t('products')} value={demand.length} />
            <Metric
              label={t('unassigned')}
              value={unassignedCount}
              tone={unassignedCount > 0 ? 'warn' : 'ok'}
            />
            <Metric label={t('orders:title')} value={orderCount} />
            <Metric
              label={t('estimated')}
              value={format.money({ minor: estimatedCost, currency: business.settings.currency }, { compact: true })}
            />
          </div>
        }
      >
        <Segmented<DayFilter>
          label={t('orders:filters.day')}
          value={dayFilter}
          onChange={setDayFilter}
          options={[
            { value: 'today', label: t('common:labels.today') },
            { value: 'tomorrow', label: t('common:labels.tomorrow') },
          ]}
        />
        <Segmented<ViewMode>
          label={t('view')}
          value={view}
          onChange={setView}
          options={[
            { value: 'products', label: t('byProduct') },
            { value: 'suppliers', label: t('bySupplier') },
          ]}
        />
      </Toolbar>

      <ListPane>
        {!isLoading && demand.length === 0 ? (
          <EmptyState title={t('empty')} body={t('emptyBody')} />
        ) : view === 'products' ? (
          <DataTable<SupplierDemandLine>
            rows={demand}
            columns={columns}
            rowKey={(row) => `${row.productId}|${row.supplierId ?? ''}`}
            rowMeta={(row) => ({
              handlingClass: productsById.get(row.productId)?.handlingClass ?? 'neutral',
              ...(row.supplierId === null ? { tone: 'warn' as const } : {}),
            })}
            loading={isLoading}
            caption={t('title')}
          />
        ) : (
          <div className="pd-group__suppliers">
            {groups.map((group) => (
              <Section
                key={group.supplierId ?? 'unassigned'}
                title={
                  <span className="row-baseline">
                    {group.supplierId === null ? (
                      <Stamp tone="warn" size="sm">{t('unassigned')}</Stamp>
                    ) : (
                      <>
                        {group.supplierName}
                        {suppliersById.get(group.supplierId)?.supplier?.standCode ? (
                          <Code>{suppliersById.get(group.supplierId)!.supplier!.standCode}</Code>
                        ) : null}
                      </>
                    )}
                    <span className="num faint">
                      {t('lineCount', { count: group.lines.length })} ·{' '}
                      {t('ordersTouched', { count: group.orderCount })}
                    </span>
                  </span>
                }
              >
                <ul className="pd-group__sheet">
                  {group.lines.map((row) => {
                    const product = productsById.get(row.productId);
                    return (
                      <li
                        key={row.productId}
                        className="pd-group__sheet-row"
                        data-hc={product?.handlingClass ?? 'neutral'}
                      >
                        <span className="pd-band" aria-hidden="true" />
                        <div className="pd-group__product">
                          <span className="truncate">
                            {product
                              ? productName(product, i18n.resolvedLanguage ?? 'en')
                              : row.productName}
                          </span>
                          <Code>{row.sku}</Code>
                        </div>
                        {demandBreakdown(row)}
                        <span className="num pd-group__sheet-qty">{format.quantity(row.total)}</span>
                      </li>
                    );
                  })}
                </ul>
              </Section>
            ))}
          </div>
        )}
      </ListPane>
    </Workbench>
  );
}
